<template>
    <div
        class="flex cursor-pointer justify-between space-x-4"
        data-testid="match-invite-notification"
        :data-from-user="notification.data.from_user.tag_name"
        @click="markAsRead"
    >
        <div class="shrink-0">
            <Avatar
                :src="notification.data.from_user.avatar"
                :alt="notification.data.from_user.tag_name"
            />
        </div>
        <div class="flex-1">
            <div class="flex items-start justify-between">
                <div class="flex-1">
                    <p class="mt-1 text-sm">
                        <strong>{{ notification.data.from_user.tag_name }}</strong>
                        {{ inviteMessage }}
                    </p>
                </div>
                <span class="text-titles ml-4 text-sm font-bold">
                    {{ formattedDate }}
                </span>
            </div>

            <div class="mt-4 grid grid-cols-5 gap-4">
                <DiagonalCutButton
                    v-if="notification.status === 'accepted'"
                    variant="primary"
                    cut-position="right-top"
                    class="col-span-5 cursor-default opacity-60"
                    disabled
                >
                    <span class="flex items-center justify-center gap-2">
                        <svg class="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                            <path
                                fill-rule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clip-rule="evenodd"
                            />
                        </svg>
                        {{ $t('notifications.accepted') }}
                    </span>
                </DiagonalCutButton>

                <DiagonalCutButton
                    v-else-if="notification.status === 'declined'"
                    variant="secondary"
                    cut-position="right-top"
                    class="col-span-5 cursor-default opacity-50"
                    disabled
                >
                    {{ $t('notifications.declined') }} {{ actionTime }}
                </DiagonalCutButton>

                <DiagonalCutButton
                    v-else-if="notification.status === 'invalidated'"
                    variant="secondary"
                    cut-position="right-top"
                    class="col-span-5 cursor-default opacity-50"
                    disabled
                >
                    <span class="text-orange-400">{{ $t('notifications.match_invite_unavailable') }}</span>
                </DiagonalCutButton>

                <DiagonalCutButton
                    v-else-if="notification.status === 'expired' || isExpired"
                    variant="secondary"
                    cut-position="right-top"
                    class="col-span-5 cursor-default opacity-40"
                    disabled
                >
                    {{ $t('notifications.match_invite_expired') }}
                </DiagonalCutButton>

                <template v-else>
                    <DiagonalCutButton
                        variant="primary"
                        cut-position="right-top"
                        class="col-span-4"
                        data-testid="match-invite-open-button"
                        @click="openInvite"
                    >
                        {{ $t('notifications.match_invite_open') }}
                    </DiagonalCutButton>
                    <DiagonalCutButton
                        variant="secondary"
                        cut-position="left-top"
                        data-testid="match-invite-notification-decline-button"
                        @click="declineInvite"
                    >
                        {{ $t('notifications.match_invite_decline') }}
                    </DiagonalCutButton>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import { mapStores } from 'pinia'
import { useMatchInviteStore } from '@/stores/matchInvite.js'
import { formatRelativeTime } from '@/utils/dateHelpers.js'
import { matchTypeName } from '@/constants/matchTypes.js'
import DiagonalCutButton from '@/components/ui/DiagonalCutButton.vue'
import Avatar from '@/components/Avatar.vue'

export default {
    name: 'MatchInviteNotification',
    components: { DiagonalCutButton, Avatar },

    props: {
        notification: {
            type: Object,
            required: true,
        },
    },

    emits: ['read', 'click'],

    created() {
        console.log(this.notification)
    },

    computed: {
        ...mapStores(useMatchInviteStore),

        formattedDate() {
            return formatRelativeTime(this.notification.created_at)
        },

        isRead() {
            return !!this.notification.read_at
        },

        isExpired() {
            if (this.notification.status === 'expired') {
                return true
            }
            const createdAt = new Date(this.notification.created_at)
            const now = new Date()
            const fifteenMinutesInMs = 15 * 60 * 1000
            return now - createdAt > fifteenMinutesInMs
        },

        isActionable() {
            return this.notification.status === 'pending' && !this.isExpired
        },

        matchtypeName() {
            return matchTypeName(
                this.notification.data?.match?.matchtype_id,
                this.notification.data?.match?.matchtype_name ||
                    this.notification.data?.match?.matchtype?.name ||
                    'a',
            )
        },

        inviteMessage() {
            if (
                this.notification.status === 'accepted' ||
                this.notification.status === 'declined'
            ) {
                return this.$t('notifications.match_invite_text')
            }
            if (this.notification.status === 'declined') {
                return this.$t('notifications.match_invite_text')
            }
            return this.$t('notifications.match_invite_game_text')
        },

    },

    methods: {
        markAsRead() {
            this.$emit('read', this.notification)
        },

        openInvite() {
            this.matchInviteStore.showInvite(this.notification)
            this.$emit('click', this.notification)
        },

        async declineInvite() {
            try {
                this.matchInviteStore.showInvite(this.notification)
                await this.matchInviteStore.declineInvite()
                this.$emit('click', this.notification)
            } catch (error) {
                console.error('Failed to decline invite:', error)
            }
        },
    },
}
</script>
